'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import type { PaketItem } from '@/lib/paket';
import { ProductBadge } from './product-badge';

interface PaketItemCardProps {
  item: PaketItem;
  onOpen: () => void;
}

/**
 * One thumbnail in "Detta ingår i paketet".
 *
 * Purpose-built rather than a variant of `ProductCard`, which wraps itself in a
 * `StoreLink` to the product and always prints a price (its `minimal` variant
 * only drops the favourite, compare and reticle rows). This card must be a
 * BUTTON — an anchor would navigate on Enter, middle-click and cmd-click no
 * matter what handler it carried, breaking the section's own promise that "du
 * lämnar inte paketet" — and it must not show a price, because the price the
 * visitor is paying is the package's. Teaching `ProductCard` a no-link,
 * no-price mode would put that risk in the component every listing page uses,
 * so this stays a separate 60 lines styled to match it.
 */
export function PaketItemCard({ item, onOpen }: PaketItemCardProps) {
  const t = useTranslations('product');

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex flex-col overflow-hidden rounded-[3px] border border-border bg-card text-left transition-colors hover:border-accent-600/50 focus-visible:border-accent-600 focus-visible:outline-none"
    >
      <div className="relative flex h-[170px] flex-none items-center justify-center overflow-hidden bg-surface-100">
        {item.image ? (
          <Image
            src={item.image.url}
            alt={item.image.label || item.name}
            fill
            className="object-contain p-2.5 mix-blend-multiply transition-transform duration-500 ease-out group-hover:scale-105"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
          />
        ) : (
          // Same fallback as the gallery: plenty of the catalogue has an empty
          // media_gallery, and a missing file drew a broken-image icon.
          <span className="text-sm text-muted">{t('noImage')}</span>
        )}

        <span className="absolute left-3 top-3 z-20">
          <ProductBadge tone="accent">{t('paketItemBadge')}</ProductBadge>
        </span>
      </div>

      {/* The card body follows ProductCard's 5px rhythm and padding so the two
          read as one family where they appear on the same page. */}
      <div className="flex flex-1 flex-col gap-[5px] px-[18px] pb-[18px] pt-4">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-foreground/50">
          {/* Falls back to the SKU for the same reason ProductCard's does: an
              empty eyebrow makes card heights jump around the grid. */}
          {item.brand ?? item.sku}
        </span>

        <h3 className="my-0 flex-1 font-sans text-[14.5px] font-semibold leading-[1.35] text-heading transition-colors group-hover:text-accent-foreground">
          {item.name}
        </h3>
      </div>
    </button>
  );
}
